import { type StaticImageData } from "next/image";

import empatheticAllTerrain from "@assets/images/innovation/empathetic-all-terrain-videostill-subtitled.webp";
import easyServiceFittedFrame from "@assets/images/innovation/easy-service-fitted-firstframe.webp";
import empatheticFormFactor from "@assets/images/innovation/empathetic-form-factor-render.webp";
import empatheticSelfCleaningMop from "@assets/images/innovation/empathetic-self-cleaning-mop-render.webp";
import surfaceHandlingVsDisc from "@assets/images/innovation/surface-handling-vs-disc-firstframe.webp";
import empatheticTangleFreeRoller from "@assets/images/innovation/empathetic-tangle-free-roller-render-lowres.webp";
import hepaBagVideoStill from "@assets/images/innovation/hepa-bag-video-still.webp";
import familyMoldWaterTank from "@assets/images/innovation/family-mold-water-tank-render.webp";
import privateByDesignFrame from "@assets/images/innovation/private-by-design-firstframe.webp";
import heroHeadshotStill from "@assets/images/innovation/hero-headshot-still.webp";
import sensorySelfImprovingHardware from "@assets/images/innovation/sensory-self-improving-hardware-dark.webp";
import selfImprovingMobileFrame from "@assets/images/innovation/self-improving-mobile-firstframe.webp";
import sensorySmartMapping from "@assets/images/innovation/sensory-smart-mapping.webp";
import smartMappingMobileFrame from "@assets/images/innovation/smart-mapping-mobile-firstframe.webp";
import visualPerceptionFrame from "@assets/images/innovation/visual-perception-firstframe.webp";
import visualPerceptionMobileFrame from "@assets/images/innovation/visual-perception-mobile-firstframe.webp";
import proofQuietWfh from "@assets/images/life/proof-7am-quiet-wfh.webp";
import { REVIEW_AVERAGE, REVIEW_COUNT } from "@/consts/reviews";
import { config } from "@/config";

export const INNOVATION_HERO = {
  eyebrow: "Clean smarter, not harder",
  title: "It all starts with seeing",
  videoMp4: `${config.media.baseUrl}/videos/innovation-hero-headshot-v1.mp4`,
  /** The video's final lit frame — the reduced-motion stand-in. */
  still: heroHeadshotStill,
  imageAlt: "Front view of the Matic robot with its two camera eyes",
};

export type InnovationTabId = "intelligence" | "design" | "cleaning";

export type InnovationFurtherReadingLink = {
  label: string;
  href: string;
};

export type InnovationTopicMedia = {
  poster: StaticImageData;
  alt: string;
  video?: string;
  /** Horizontal focal point for the card's cover crop. */
  focalX?: string;
  /**
   * The card is 3:4 below `sd` and 3:2 above it, so a landscape cut has to be
   * cropped hard on narrow screens. Cards with a portrait cut delivered play it
   * there instead; the rest keep the landscape one at every width.
   */
  mobile?: { poster: StaticImageData; video: string };
};

export type InnovationTopicDetail = {
  prefix?: string;
  lead: string;
  body: string;
};

export type InnovationTopic = {
  id: string;
  title: string;
  body: string;
  details: readonly InnovationTopicDetail[];
  furtherReading: readonly InnovationFurtherReadingLink[];
  /** Absent only where the design has no asset yet. */
  media?: InnovationTopicMedia;
};

export type InnovationTabGroup = {
  id: InnovationTabId;
  label: string;
  header: string;
  topics: readonly InnovationTopic[];
};

export const INNOVATION_FURTHER_READING_LABEL = "Further reading";

export const INNOVATION_TOPIC_TOGGLE = {
  collapsedLabel: "+ See more",
  expandedLabel: "− See less",
};

const THE_DESIGN_LINK: InnovationFurtherReadingLink = {
  label: "Reimagining Home Cleaning with Matic: The Design",
  href: "/blog/the-design",
};

const SEVEN_YEAR_JOURNEY_LINK: InnovationFurtherReadingLink = {
  label:
    "Inside Matic's 7-Year Journey To Create Vision-Only Robots That See the World Like Humans",
  href: "/blog/vision-first-robotics-how-matic-is-reimagining-home-automation",
};

const RELEASE_NOTES_LINK: InnovationFurtherReadingLink = {
  label: "Release Notes",
  href: "/blog",
};

const PRIVACY_IS_WHY_LINK: InnovationFurtherReadingLink = {
  label: "Privacy Isn’t a Feature. It’s Why We Built Matic",
  href: "/blog/privacy-isnt-a-feature-its-why-we-built-matic",
};

const MOST_PRIVATE_ROBOT_LINK: InnovationFurtherReadingLink = {
  label:
    "The World’s First Robot Reimagined to Defend the Privacy of Your Home",
  href: "/blog/why-matic-is-the-most-private-and-secure-robot-vacuum",
};

const AMA_HARDWARE_DESIGN_LINK: InnovationFurtherReadingLink = {
  label: "AMA with Matic’s Head of Hardware Design",
  href: "/blog/ask-me-anything-matic-hardware-design",
};

const WHY_BRUSHROLL_DIFFERENT_LINK: InnovationFurtherReadingLink = {
  label:
    "Why Matic’s Brushroll is Different—And Why It Matters for Your Floors",
  href: "https://maticrobots.com/blog/why-matic-brushroll-is-different",
};

const MOPPING_MAGIC_LINK: InnovationFurtherReadingLink = {
  label: "The Magic Behind Matic’s Mopping",
  href: "/blog/the-magic-behind-matics-mopping",
};

const HEPA_SCIENCE_LINK: InnovationFurtherReadingLink = {
  label: "The Science Behind Matic’s Hepa-12 Bags",
  href: "/blog/matic-robot-vacuum-hepa-bag-2",
};

const SERENE_ENGINEERING_LINK: InnovationFurtherReadingLink = {
  label: "The engineering behind Matic Serene's smoother sound",
  href: "/blog/engineering-behind-matic-serene",
};

export const INNOVATION_TABS: readonly [
  InnovationTabGroup,
  ...InnovationTabGroup[],
] = [
  {
    id: "intelligence",
    label: "Intelligence",
    header: "Sees and hears like you. Intelligence becomes intuition.",
    topics: [
      {
        id: "visual-perception",
        title: "Visual perception",
        body: "Matic uses cutting-edge computer vision to recognize everyday objects, detect low obstacles, and adjust its path in real time, even in low light and clutter.",
        details: [
          {
            lead: "Five RGB-IR cameras",
            body: " are angled at 45 degrees to see more of the room ahead, capturing color and infrared light so Matic can map and clean in low light or darkness.",
          },
          {
            lead: "Object detection",
            body: " identifies everyday objects like shoes and furniture legs, so Matic cleans around them, not into them.",
          },
          {
            lead: "Low-obstacle detection",
            body: " spots items as small as ½ inch high or wide, including wires, toys, and stray socks, and adjusts Matic’s path around them in real-time.",
          },
          {
            lead: "Pet waste avoidance",
            body: " is currently in beta, helping Matic recognize and steer clear of pet messes.",
          },
        ],
        furtherReading: [SEVEN_YEAR_JOURNEY_LINK, THE_DESIGN_LINK],
        media: {
          poster: visualPerceptionFrame,
          video: `${config.media.baseUrl}/videos/innovation-visual-perception.mp4`,
          alt: "Matic’s live map beside an overhead view of it steering past scattered blocks",
          focalX: "46%",
          mobile: {
            poster: visualPerceptionMobileFrame,
            video: `${config.media.baseUrl}/videos/innovation-visual-perception-mobile.mp4`,
          },
        },
      },
      {
        id: "dynamic-mapping",
        title: "Dynamic mapping",
        body: "Matic uses spatial awareness to build a detailed 3D map of your home and locate itself within it. If furniture moves or rooms change, the map updates with them. A 3,000-square-foot home maps in under 20 minutes, furniture and all.",
        details: [
          {
            lead: "A true 3D model",
            body: " turns camera images into 1 cm³ voxels (three-dimensional pixels) so Matic can understand depth and distance throughout your home.",
          },
          {
            lead: "Long-term localization",
            body: " lets Matic recognize a place it has mapped before and quickly determine where it is, even if the robot has been moved.",
          },
          {
            lead: "No dock dependency",
            body: " means Matic’s map is anchored to your home, not just the route it took from its base. The dock is another point on the map, rather than the reference point for the entire map.",
          },
        ],
        furtherReading: [THE_DESIGN_LINK, SEVEN_YEAR_JOURNEY_LINK],
        /* The clip's own first frame, so the card holds still on exactly what it
           then plays. The still it replaced was a low-res grab with the video's
           subtitle burnt into it, which read as a caption the page never wrote. */
        media: {
          poster: sensorySmartMapping,
          video: `${config.media.baseUrl}/videos/innovation-smart-mapping.mp4`,
          alt: "A 3D map of a home generated by Matic",
          mobile: {
            poster: smartMappingMobileFrame,
            video: `${config.media.baseUrl}/videos/innovation-smart-mapping-mobile.mp4`,
          },
        },
      },
      {
        id: "surface-handling",
        title: "Surface handling",
        body: "Matic recognizes rugs, carpet, and hard floors, and cleans each one appropriately. Run Vacuum + Mop, and it handles the rest: vacuuming everywhere first, then mopping only hard floors.",
        details: [
          {
            lead: "A surface map that stays current",
            body: " records where rugs, carpet, and hard floors are, then updates when rugs move or rooms change.",
          },
          {
            lead: "One system handles both jobs",
            body: " by carrying clean water while collecting dry debris and dirty mop water into the waste bag, so Matic can run without a tank swap.",
          },
        ],
        furtherReading: [SEVEN_YEAR_JOURNEY_LINK],
        media: {
          poster: surfaceHandlingVsDisc,
          video: `${config.media.baseUrl}/videos/innovation-surface-handling-vs-disc.mp4`,
          alt: "Split screen: a disc robot stalls at the edge of a rug while Matic drives straight up onto it",
        },
      },
      {
        id: "built-to-get-better",
        title: "Built to get better",
        body: "The onboard NVIDIA processor powers advanced vision and navigation, with headroom to spare. Regular software updates put it to work, adding capabilities and improving how Matic sees, moves, and cleans.",
        details: [
          {
            lead: "New releases",
            body: " have introduced capabilities such as mirror detection, better wire detection, faster edge cleaning, and more reliable recovery from interruptions.",
          },
          {
            lead: "Matic Cues",
            body: " enabled voice and gesture capabilities on all 10,000+ Matic robots ever shipped, in August 2026.",
          },
        ],
        furtherReading: [RELEASE_NOTES_LINK],
        // TODO(agency): FPO photo — wants NVIDIA-chip render or x-ray peel animation.
        media: {
          poster: sensorySelfImprovingHardware,
          video: `${config.media.baseUrl}/videos/innovation-self-improving.mp4`,
          alt: "Matic’s internal hardware plate floating in a studio",
          mobile: {
            poster: selfImprovingMobileFrame,
            video: `${config.media.baseUrl}/videos/innovation-self-improving-mobile.mp4`,
          },
        },
      },
      {
        id: "private-by-design",
        title: "Private by design",
        body: "Privacy is a design decision, not a setting. Camera feeds, audio, maps, and cleaning history stay on Matic by default, where they are processed on-device and stay private–even from us. No cloud.",
        details: [
          {
            lead: "Everything stays encrypted.",
            body: " Your Matic and app communicate securely, and our servers can connect them without reading what passes between them. If you opt out of troubleshooting data, we see only the buttons you press.",
          },
          {
            lead: "You choose what to share.",
            body: " Error clips stay private unless you decide to send them to Matic for troubleshooting. You can preview every clip before sharing it.",
          },
        ],
        furtherReading: [PRIVACY_IS_WHY_LINK, MOST_PRIVATE_ROBOT_LINK],
        media: {
          poster: privateByDesignFrame,
          video: `${config.media.baseUrl}/videos/innovation-private-by-design.mp4`,
          alt: "Matic’s cameras dimming until their indicator lights go dark",
        },
      },
    ],
  },
  {
    id: "design",
    label: "Design",
    header: "Made to move through your home. Designed to belong in it.",
    topics: [
      {
        id: "form-factor",
        title: "Form factor",
        body: "Matic’s higher body gives its cameras an optimal vantage point, while the low, rectangular cleaning head reaches into corners, along edges, and under toe-kicks for better cleaning.",
        details: [
          {
            lead: "Split-level architecture",
            body: " keeps cameras and onboard computing in the 8” upright body, while the cleaning hardware stays close to the floor.",
          },
          {
            prefix: "A 3” ",
            lead: "flat front edge",
            body: " brings the full brush roll and suction path close to walls and corners instead of relying on a small side brush to reach them.",
          },
          {
            prefix: "A ",
            lead: "compact charging dock",
            body: " takes up less room in your space, and avoids the footprint of a large self-emptying base station.",
          },
        ],
        furtherReading: [THE_DESIGN_LINK],
        media: {
          poster: empatheticFormFactor,
          video: `${config.media.baseUrl}/videos/innovation-form-factor.mp4`,
          alt: "Close crop of Matic’s rounded body",
        },
      },
      {
        id: "quiet",
        title: "Quiet",
        body: "Matic brings deep-cleaning power down to a hush. At about 55 dB while vacuuming, its soft, fan-like sound fades into the background. Matic Serene takes quiet further at 52 dB, with a softer, smoother sound.",
        details: [
          {
            prefix: "The ",
            lead: "brush roll loosens fine dirt",
            body: " before suction pulls it into the bag, reducing the suction power needed for effective cleaning.",
          },
          {
            prefix: "A ",
            lead: "softer sound profile",
            body: " minimizes irritating high-pitched tones, producing noise closer to a steady hum.",
          },
        ],
        furtherReading: [SERENE_ENGINEERING_LINK, THE_DESIGN_LINK],
        media: {
          poster: proofQuietWfh,
          video: `${config.media.baseUrl}/videos/innovation-quiet.mp4`,
          alt: "Matic cleaning quietly while someone works from home",
        },
      },
      {
        id: "easy-access",
        title: "Easy access",
        body: "Lift one top flap and everything you need is within reach: brush roll, mop, water tank, and sealed bag. Routine upkeep stays clean, simple, and upright, with no bulky base station to maintain.",
        details: [
          {
            lead: "Top-down access",
            body: " opens the entire cleaning head without requiring you to flip Matic over.",
          },
          {
            lead: "One simple lid",
            body: " gives you direct access to the removable water tank and sealed HEPA bag.",
          },
          {
            lead: "No maintenance-heavy base station",
            body: " means no large dirty-water tank or extra dock system to empty, rinse, or deodorize.",
          },
        ],
        furtherReading: [AMA_HARDWARE_DESIGN_LINK, THE_DESIGN_LINK],
        media: {
          // TODO(agency): reframed to 3:4 to keep the baked-in HEPA callout
          // on screen; drop once a caption-free cut is delivered.
          poster: easyServiceFittedFrame,
          video: `${config.media.baseUrl}/videos/innovation-easy-service.mp4`,
          alt: "Matic with its top lid open for easy service access",
        },
      },
      {
        id: "mobility",
        title: "Mobility",
        body: "From hardwood to thick rugs to the thresholds between them, Matic keeps moving. Large wheels carry it across changes in height, while an articulating cleaning head adjusts to the floor beneath it.",
        details: [
          {
            lead: "Large drive wheels",
            body: " cross floor transitions and thresholds up to 0.9” high, helping Matic move smoothly between rooms and surfaces.",
          },
          {
            lead: "Independent head actuation",
            body: " raises, lowers, and changes the angle of the cleaning head as floor height changes.",
          },
          {
            lead: "Smoother transition handling",
            body: " helps Matic move between carpet and hard floors with less hesitation and fewer interruptions.",
          },
        ],
        furtherReading: [THE_DESIGN_LINK],
        // TODO(agency): video still has a baked subtitle — needs clean version.
        media: {
          poster: empatheticAllTerrain,
          video: `${config.media.baseUrl}/videos/innovation-all-terrain.mp4`,
          alt: "Matic climbing a threshold between rooms",
        },
      },
    ],
  },
  {
    id: "cleaning",
    label: "Cleaning",
    header: "A deeper clean, built into every detail.",
    topics: [
      {
        id: "bristle-free-brush-roll",
        title: "Tangle-free brush roll",
        body: "A wide, smooth brush roll keeps hair from wrapping around it. Generously spaced flaps pick up debris from fine dust to cereal, while the soft bristle-free construction stays gentle on hard floors, rugs, and carpet.",
        details: [
          {
            lead: "Wide, consistent diameter",
            body: " gives short hair too little length to wrap, while longer strands stay loose enough to move into the bag.",
          },
          {
            lead: "Large flaps",
            body: " with generous clearance make room for bigger debris like popcorn and leaves instead of pushing it ahead.",
          },
          {
            lead: "Multiple passes",
            body: " when needed let Matic clean thoroughly without relying on aggressive suction or stiff bristles.",
          },
          {
            lead: "Smooth rubber",
            body: " construction makes the roll easy to wipe clean.",
          },
        ],
        furtherReading: [WHY_BRUSHROLL_DIFFERENT_LINK],
        // TODO(agency): thumbnail-res render — needs hi-res front render.
        media: {
          poster: empatheticTangleFreeRoller,
          video: `${config.media.baseUrl}/videos/innovation-tangle-free-roller.mp4`,
          alt: "Matic’s tangle-free brush roller",
        },
      },
      {
        id: "self-cleaning-mop",
        title: "Self-cleaning mop",
        body: "The rotating microfiber mop scrubs with fresh water while a built-in wringer squeezes out dirty water, sending it into the HEPA bag. Staying cleaner as it moves, it lifts messes instead of spreading them across the floor.",
        details: [
          {
            lead: "Precisely controlled water flow",
            body: " uses an electronic pump to dispense the right amount of water onto the mop roll.",
          },
          {
            lead: "Powered microfiber scrubbing",
            body: " combines the rotating roll, the weight of Matic’s cleaning head, and backward motion to work on sticky and dried-on stains.",
          },
          {
            lead: "Continuous wringing and suction",
            body: " remove dirty water from the mop fibers and carry it through internal tubes into the HEPA bag alongside dry debris.",
          },
        ],
        furtherReading: [MOPPING_MAGIC_LINK],
        media: {
          poster: empatheticSelfCleaningMop,
          video: `${config.media.baseUrl}/videos/innovation-mop.mp4`,
          alt: "Matic’s self-cleaning mop roll seen from underneath",
        },
      },
      {
        id: "hepa-12-bag",
        title: "HEPA-12 Bag",
        body: "Every bag’s built-in filter captures 99.5% of particles down to 0.3 microns from released air. Dry debris and dirty mop water go into the bag, where gel-forming salts solidify the liquid. A full bag lifts out cleanly, with no dirty tank to empty.",
        details: [
          {
            lead: "Dual-material construction",
            body: " combines a breathable filtration layer for vacuum airflow with a liquid-tight film that securely contains wet waste.",
          },
          {
            lead: "Automatic drying",
            body: " after every clean uses low-power suction to reduce moisture inside the bag and help prevent mold.",
          },
          {
            lead: "Adaptive suction and fill sensing",
            body: " monitor airflow, pressure, and bag capacity, maintaining performance as the bag fills and notifying you when it is time for a replacement.",
          },
        ],
        furtherReading: [HEPA_SCIENCE_LINK],
        // TODO(agency): final HEPA-bag asset may add the proposed removal
        // motion; the current front render remains the deliberate placeholder.
        media: {
          // The video's own first frame, so reduced motion shows the render
          // the animation opens on rather than a different product shot.
          poster: hepaBagVideoStill,
          video: `${config.media.baseUrl}/videos/innovation-hepa.mp4`,
          alt: "Matic’s HEPA-certified vacuum bag with its sealed inlet",
        },
      },
      {
        id: "fresh-water-system",
        title: "Fresh water system",
        body: "The removable water tank’s cap houses a replaceable antimicrobial silver-ion cartridge, limiting microbial growth to keep water fresher between cleans.",
        details: [
          {
            lead: "A removable 600 mL tank",
            body: " holds enough water to mop up to 1,000 square feet in a single run and lifts out for easy filling and rinsing.",
          },
          {
            lead: "Approved cleaning solution",
            body: " can be added at one teaspoon per tank for better mopping.",
          },
          {
            lead: "Automatic descaling alerts",
            body: " tell you when to use a dedicated descaling powder to clear mineral buildup from Matic’s internal mopping system.",
          },
          {
            lead: "Replaceable cartridges",
            body: " should be swapped every three months.",
          },
        ],
        furtherReading: [MOPPING_MAGIC_LINK, THE_DESIGN_LINK],
        // TODO(agency): explore whether the silver-ion bead cartridge can be
        // depicted in motion; the delivered water-tank render remains static.
        media: {
          poster: familyMoldWaterTank,
          video: `${config.media.baseUrl}/videos/innovation-water-tank.mp4`,
          alt: "Matic’s water tank with its silver ion bead cartridge",
        },
      },
    ],
  },
];

export type RoadmapStatus = "released" | "upcoming";

type RoadmapEntry = {
  period: string;
  description: string;
  status: RoadmapStatus;
};

export const ROADMAP = {
  heading: "A robot that only gets better",
  subhead:
    "Your Matic keeps gaining new features and capabilities through software updates.",
  timeline: [
    {
      period: "Summer 2025",
      description:
        "Pet waste detection entered beta, with an in-app toggle for customers.",
      status: "released",
    },
    {
      period: "Fall 2025",
      description:
        "Obstacle detection sharpens to one inch — thin chair legs, scales, small toys.",
      status: "released",
    },
    {
      period: "Spring 2026",
      description:
        "Matic handles mirrors and reflective surfaces more reliably.",
      status: "released",
    },
    {
      period: "Summer 2026",
      description:
        "“Hey Matic, clean this.” Voice and gestures arrive with Matic Cues.",
      status: "released",
    },
    {
      period: "Coming soon",
      description:
        "Matic gets better at understanding the objects and activity around it, so cleaning gets more contextual.",
      status: "upcoming",
    },
  ] satisfies readonly RoadmapEntry[],
};

type BattlecardRow = {
  category: string;
  matic: string;
  maticDetail: string;
  robodisc: string;
  robodiscDetail?: string;
};

export const BATTLECARD = {
  heading: "How it compares",
  maticColumnLabel: "Matic",
  robodiscColumnLabel: "Traditional Disc Vacuums",
  rows: [
    {
      category: "Voice & Gesture",
      matic: "Speak naturally or gesture directly to Matic with Matic Cues",
      maticDetail: "Works out of the box. No smart home setup needed.",
      robodisc:
        "Preset offline voice commands on select models; no gesture control",
    },
    {
      category: "Accuracy",
      matic:
        "Visual SLAM rapidly builds a full 3D map — 99.999% accuracy across lighting and surfaces",
      maticDetail: "3D-maps 3,000 square feet in under 20 minutes",
      robodisc:
        "Typically navigate from a 2D floor plan without a persistent, continuously updated 3D visual model of the full home.",
    },
    {
      category: "Privacy & Security",
      matic:
        "Sensitive home data stays on-device. At home, your phone connects directly to Matic. Away, Matic’s servers relay an encrypted connection they cannot read.",
      maticDetail: "Troubleshooting data shared only when you choose",
      robodisc:
        "Cloud-connected architecture sends maps and device data through manufacturer servers as part of normal operation, even when some processing happens locally.",
    },
    {
      category: "Obstacle Detection",
      matic:
        "Uses smart vision to recognize and route around wires, rug tassels, toys, and other floor-level clutter",
      maticDetail: "Sees obstacles as small as ½″ high or wide",
      robodisc:
        "Obstacle sensing varies by model; thin, flexible, and low-profile items remain the hardest to detect reliably",
    },
    {
      category: "Surface handling",
      matic:
        "Vacuums rugs and carpet with the mop fully out of contact, then mops only hard floors",
      maticDetail: "Rugs stay dry, automatically",
      robodisc:
        "Lift-only systems carry damp mop pads above carpet as the robot vacuums",
      robodiscDetail: "Thick or uneven rug fibers may still contact the mop",
    },
    {
      category: "Mobility",
      matic:
        "Large wheels and an actuating cleaning head move across hard floors, rugs, and raised transitions",
      maticDetail: "Climbs door thresholds up to 0.9″",
      robodisc:
        "Low-clearance discs struggle with thick rugs and raised thresholds",
    },
    {
      category: "Quiet",
      matic:
        "Matic: ~55 dB while vacuuming\nMatic Serene: ~52 dB while vacuuming",
      maticDetail: "Quieter than a typical human conversation",
      robodisc: "60–75+ decibels while vacuuming",
      robodiscDetail: "Even louder self-emptying blast",
    },
    {
      category: "Always evolving",
      matic: "Software updates ship directly to your Matic, weekly to monthly",
      maticDetail: "Matic continuously gets smarter",
      robodisc: "Designed to be made obsolete by the next product in line",
    },
  ] satisfies readonly BattlecardRow[],
};

type ReviewQuote = {
  quote: string;
  name: string;
  location: string;
};

export const REVIEW_CAROUSEL = {
  eyebrow: `Rated ${REVIEW_AVERAGE}/5 across ${REVIEW_COUNT}+ reviews`,
  quotes: [
    {
      quote:
        "I absolutely love my Matic robot. It's the first time I've had a robot vacuum that can operate in the chaos of having 3 kids and 3 dogs.",
      name: "Wayne N.",
      location: "Matic customer since December 2023",
    },
    {
      quote:
        "Matic is the best robot vacuum on the market and I just love watching it clean. With its googly eyes on of course.",
      name: "Frank C.",
      location: "Matic customer since March 2025",
    },
    {
      quote:
        "I knew my wife would eventually see the magic of having clean floors every morning.  She absolutely loves this thing.",
      name: "Travis S.",
      location: "Matic customer since May 2025",
    },
  ] satisfies readonly ReviewQuote[],
};
